import { useProductLists } from '@contexts/ProductListsContext';
import { analytics } from '@lib/analytics/page-events';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { GetAssetByIdResponse } from '@lib/types/api/next-api/responses';
import { useEffect, useRef } from 'react';

type FavoriteProps = {
  showFavorite: boolean;
  isFavorite: boolean | null | undefined;
  onFavorite: (() => void) | undefined;
};

export function useProductListFavorites(products: GetAssetByIdResponse[]) {
  const { isProductInList, toggleProductList, fetchBatchProductMembership } =
    useProductLists();
  const { flags } = useFlags();
  const isEnabled = flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const prevKeyRef = useRef('');

  useEffect(() => {
    if (!isEnabled || products.length === 0) return;

    const ids = products.map((product) => product.id).filter(Boolean);
    const key = [...ids].sort().join(',');
    if (key && key !== prevKeyRef.current) {
      prevKeyRef.current = key;
      fetchBatchProductMembership(ids);
    }
  }, [isEnabled, products, fetchBatchProductMembership]);

  const getFavoriteProps = (product: GetAssetByIdResponse): FavoriteProps => ({
    showFavorite: isEnabled,
    isFavorite: isEnabled ? isProductInList(product.id) : undefined,
    onFavorite: isEnabled
      ? () => {
          analytics.pushAddFavoriteEvent(product.name);
          toggleProductList({ id: product.id, name: product.name });
        }
      : undefined,
  });

  return { isEnabled, getFavoriteProps };
}
